import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { ArrowRight, Video, Scissors, Play, TrendingUp, Users, Clock } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { updateMetaTags } from '@/utils/seoUtils';

const VideoEditingService = () => {
  const { currentLanguage } = useLanguage();
  const { city } = useParams<{ city?: string }>();

  useEffect(() => {
    const title = {
      de: `Video Editing Services ${city ? `in ${city}` : 'Schweiz'} | Future Level`,
      en: `Video Editing Services ${city ? `in ${city}` : 'Switzerland'} | Future Level`,
      fr: `Services de Montage Vidéo ${city ? `à ${city}` : 'Suisse'} | Future Level`,
      it: `Servizi di Editing Video ${city ? `a ${city}` : 'Svizzera'} | Future Level`
    };

    const description = {
      de: `Professionelle Video-Bearbeitung für Schweizer Unternehmen. Ads, Reels, Corporate Videos & mehr. AI-optimiert, schnell, kosteneffizient.`,
      en: `Professional video editing for Swiss businesses. Ads, reels, corporate videos & more. AI-optimized, fast, cost-effective.`,
      fr: `Montage vidéo professionnel pour entreprises suisses. Pubs, reels, vidéos corporate & plus. Optimisé IA, rapide, rentable.`,
      it: `Editing video professionale per aziende svizzere. Pubblicità, reel, video aziendali & altro. Ottimizzato IA, veloce, conveniente.`
    };

    updateMetaTags({
      title: title[currentLanguage],
      description: description[currentLanguage],
      canonicalUrl: `https://futurelevel.ch/${currentLanguage}/video-editing${city ? `/${city}` : ''}/`,
      language: currentLanguage
    });
  }, [currentLanguage, city]);

  const services = [
    {
      icon: Video,
      title: {
        de: 'Social Media Videos',
        en: 'Social Media Videos',
        fr: 'Vidéos Médias Sociaux',
        it: 'Video Social Media'
      },
      description: {
        de: 'Instagram Reels, TikTok, YouTube Shorts - optimiert für maximales Engagement',
        en: 'Instagram Reels, TikTok, YouTube Shorts - optimized for maximum engagement',
        fr: 'Instagram Reels, TikTok, YouTube Shorts - optimisés pour engagement maximum',
        it: 'Instagram Reels, TikTok, YouTube Shorts - ottimizzati per massimo engagement'
      },
      price: 'CHF 150-400'
    },
    {
      icon: Play,
      title: {
        de: 'Werbevideos',
        en: 'Advertisement Videos',
        fr: 'Vidéos Publicitaires',
        it: 'Video Pubblicitari'
      },
      description: {
        de: 'Google Ads, Meta Ads, LinkedIn - konversionsoptimierte Videoanzeigen',
        en: 'Google Ads, Meta Ads, LinkedIn - conversion-optimized video advertisements',
        fr: 'Google Ads, Meta Ads, LinkedIn - publicités vidéo optimisées conversion',
        it: 'Google Ads, Meta Ads, LinkedIn - video pubblicitari ottimizzati conversione'
      },
      price: 'CHF 300-800'
    },
    {
      icon: Scissors,
      title: {
        de: 'Corporate Videos',
        en: 'Corporate Videos',
        fr: 'Vidéos Corporate',
        it: 'Video Aziendali'
      },
      description: {
        de: 'Unternehmensvideos, Schulungen, Präsentationen - professionell geschnitten',
        en: 'Company videos, training, presentations - professionally edited',
        fr: 'Vidéos d\'entreprise, formations, présentations - montage professionnel',
        it: 'Video aziendali, formazione, presentazioni - editing professionale'
      },
      price: 'CHF 500-1200'
    }
  ];

  const benefits = [
    {
      icon: Clock,
      title: {
        de: 'Schnelle Lieferung',
        en: 'Fast Delivery',
        fr: 'Livraison Rapide',
        it: 'Consegna Veloce'
      },
      description: {
        de: '24-48h Turnaround für Standard-Projekte',
        en: '24-48h turnaround for standard projects',
        fr: 'Délai 24-48h pour projets standards',
        it: 'Consegna 24-48h per progetti standard'
      }
    },
    {
      icon: TrendingUp,
      title: {
        de: 'AI-Optimiert',
        en: 'AI-Optimized',
        fr: 'Optimisé IA',
        it: 'Ottimizzato IA'
      },
      description: {
        de: 'Automatisierte Workflows für Effizienz',
        en: 'Automated workflows for efficiency',
        fr: 'Workflows automatisés pour efficacité',
        it: 'Flussi automatizzati per efficienza'
      }
    },
    {
      icon: Users,
      title: {
        de: 'Swiss Quality',
        en: 'Swiss Quality',
        fr: 'Qualité Suisse',
        it: 'Qualità Svizzera'
      },
      description: {
        de: 'Schweizer Standards, internationale Expertise',
        en: 'Swiss standards, international expertise',
        fr: 'Standards suisses, expertise internationale',
        it: 'Standard svizzeri, competenza internazionale'
      }
    }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-grow pt-20">
        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-br from-background to-background/80 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/20 via-background to-background"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge variant="outline" className="mb-4">
                <Video className="w-4 h-4 mr-2" />
                {currentLanguage === 'de' ? 'Video Editing' : 
                 currentLanguage === 'fr' ? 'Montage Vidéo' :
                 currentLanguage === 'it' ? 'Editing Video' : 'Video Editing'}
              </Badge>
              
              <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent">
                {currentLanguage === 'de' ? 'Professionelle Video-Bearbeitung für Schweizer Unternehmen' :
                 currentLanguage === 'fr' ? 'Montage Vidéo Professionnel pour Entreprises Suisses' :
                 currentLanguage === 'it' ? 'Editing Video Professionale per Aziende Svizzere' :
                 'Professional Video Editing for Swiss Businesses'}
              </h1>
              
              <p className="text-xl md:text-2xl text-muted-foreground mb-8 leading-relaxed">
                {currentLanguage === 'de' ? 'Von Social Media Clips bis Corporate Videos - AI-optimierte Bearbeitung in Schweizer Qualität' :
                 currentLanguage === 'fr' ? 'Des clips médias sociaux aux vidéos corporate - montage optimisé IA en qualité suisse' :
                 currentLanguage === 'it' ? 'Da clip social media a video aziendali - editing ottimizzato IA in qualità svizzera' :
                 'From social media clips to corporate videos - AI-optimized editing in Swiss quality'}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <Button size="lg" className="bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary">
                  {currentLanguage === 'de' ? 'Kostenloses Angebot' :
                   currentLanguage === 'fr' ? 'Devis Gratuit' :
                   currentLanguage === 'it' ? 'Preventivo Gratuito' :
                   'Free Quote'}
                  <ArrowRight className="ml-2" size={20} />
                </Button>
                <Button variant="outline" size="lg">
                  {currentLanguage === 'de' ? 'Portfolio ansehen' :
                   currentLanguage === 'fr' ? 'Voir Portfolio' :
                   currentLanguage === 'it' ? 'Vedi Portfolio' :
                   'View Portfolio'}
                </Button>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">24h</div>
                  <div className="text-sm text-muted-foreground">
                    {currentLanguage === 'de' ? 'Express-Lieferung' :
                     currentLanguage === 'fr' ? 'Livraison Express' :
                     currentLanguage === 'it' ? 'Consegna Express' :
                     'Express Delivery'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">500+</div>
                  <div className="text-sm text-muted-foreground">
                    {currentLanguage === 'de' ? 'Videos erstellt' :
                     currentLanguage === 'fr' ? 'Vidéos créées' :
                     currentLanguage === 'it' ? 'Video creati' :
                     'Videos Created'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">90%</div>
                  <div className="text-sm text-muted-foreground">
                    {currentLanguage === 'de' ? 'Zeitersparnis' :
                     currentLanguage === 'fr' ? 'Gain de Temps' :
                     currentLanguage === 'it' ? 'Risparmio Tempo' :
                     'Time Saved'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary">4.9★</div>
                  <div className="text-sm text-muted-foreground">
                    {currentLanguage === 'de' ? 'Kundenbewertung' :
                     currentLanguage === 'fr' ? 'Note Client' :
                     currentLanguage === 'it' ? 'Valutazione Cliente' :
                     'Client Rating'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                {currentLanguage === 'de' ? 'Unsere Video-Editing Services' :
                 currentLanguage === 'fr' ? 'Nos Services de Montage Vidéo' :
                 currentLanguage === 'it' ? 'I Nostri Servizi di Editing Video' :
                 'Our Video Editing Services'}
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8">
                {services.map((service, index) => {
                  const Icon = service.icon;
                  return (
                    <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                      <div className="text-center mb-4">
                        <Icon className="w-12 h-12 text-primary mx-auto mb-4" />
                        <h3 className="text-xl font-semibold mb-2">
                          {service.title[currentLanguage]}
                        </h3>
                        <p className="text-muted-foreground mb-4">
                          {service.description[currentLanguage]}
                        </p>
                        <div className="text-lg font-bold text-primary mb-4">
                          {service.price}
                        </div>
                        <Button className="w-full">
                          {currentLanguage === 'de' ? 'Mehr erfahren' :
                           currentLanguage === 'fr' ? 'En savoir plus' :
                           currentLanguage === 'it' ? 'Scopri di più' :
                           'Learn More'}
                        </Button>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 bg-background/50">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12">
                {currentLanguage === 'de' ? 'Warum Future Level für Video Editing?' :
                 currentLanguage === 'fr' ? 'Pourquoi Future Level pour le Montage Vidéo?' :
                 currentLanguage === 'it' ? 'Perché Future Level per l\'Editing Video?' :
                 'Why Future Level for Video Editing?'}
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8">
                {benefits.map((benefit, index) => {
                  const Icon = benefit.icon;
                  return (
                    <div key={index} className="text-center">
                      <Icon className="w-12 h-12 text-primary mx-auto mb-4" />
                      <h3 className="text-xl font-semibold mb-2">
                        {benefit.title[currentLanguage]}
                      </h3>
                      <p className="text-muted-foreground">
                        {benefit.description[currentLanguage]}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-6">
              {currentLanguage === 'de' ? 'Bereit für professionelle Videos?' :
               currentLanguage === 'fr' ? 'Prêt pour des vidéos professionnelles?' :
               currentLanguage === 'it' ? 'Pronti per video professionali?' :
               'Ready for Professional Videos?'}
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              {currentLanguage === 'de' ? 'Lassen Sie uns Ihre Inhalte in hochwertige Videos verwandeln, die Ihr Publikum begeistern.' :
               currentLanguage === 'fr' ? 'Laissez-nous transformer votre contenu en vidéos de haute qualité qui captivent votre audience.' :
               currentLanguage === 'it' ? 'Lasciaci trasformare i tuoi contenuti in video di alta qualità che affascinano il tuo pubblico.' :
               'Let us transform your content into high-quality videos that captivate your audience.'}
            </p>
            <Button size="lg" asChild className="bg-gradient-to-r from-primary to-primary/90">
              <a href={`/${currentLanguage}/kontakt/`}>
                {currentLanguage === 'de' ? 'Projekt starten' :
                 currentLanguage === 'fr' ? 'Démarrer le projet' :
                 currentLanguage === 'it' ? 'Inizia progetto' :
                 'Start Project'}
                <ArrowRight className="ml-2" size={20} />
              </a>
            </Button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default VideoEditingService;